import { useState, useEffect, useRef, type ReactNode } from 'react';
import { useParams, useNavigate, Link, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth, SignedIn, SignedOut } from '../../auth';
import { CalendarButtons } from './CalendarButtons';
import { NavBar } from '../../components/NavBar';
import { SharedFooter } from '../../components/SharedFooter';
import { FollowButton } from '../follow/FollowButton';
import { ApplicationsSection } from '../dashboard/ApplicationsSection';
import type { RevenueSplitMode } from '@modelcast/types';
import { authHeaders, jsonAuthHeaders, signInMessageForAuthStatus } from '../../shared/authHeaders';
import {
  invitationPageContentClassName,
  invitationPageRolePanelClassName,
} from './invitationPageLayout';
import { EventImageCredit } from '../events/EventImageCredit';
import { logVideoDiagnostic } from '../video/videoDiagnostics';
import {
  getRoomSessionDiagnosticContext,
  storeJoinedRoomSession,
} from '../room/joinSession';

interface Attendee {
  name: string | null;
  initial: string;
  photoUrl: string | null;
  profileId: string | null;
}

interface ProfilePhoto {
  id: string;
  url: string;
  displayOrder: number;
}

interface ModelProfile {
  id: string;
  username: string | null;
  displayName: string;
  bio: string | null;
  instagram: string | null;
  website: string | null;
  country: string | null;
  photos: ProfilePhoto[];
}

interface HostProfile {
  id: string;
  displayName: string;
  bio: string | null;
  instagram: string | null;
  website: string | null;
  country: string | null;
  photos: ProfilePhoto[];
}

interface InvitationData {
  id: string;
  name: string;
  description: string | null;
  code: string;
  scheduledAt: string | null;
  endsAt: string | null;
  timezone: string | null;
  duration: number | null;
  hasStarted: boolean;
  isClosed: boolean;
  cancelledAt: string | null;
  cancellationReason: string | null;
  isInvited: boolean;
  isBlocked: boolean;
  isHost: boolean;
  isModel: boolean;
  isTutored: boolean;
  modelOption: 'none' | 'self' | 'invite' | 'ad';
  viewerRole: 'host' | 'model' | 'attendee' | 'guest';
  hostName: string | null;
  hostProfileId: string | null;
  hostProfile: HostProfile | null;
  modelName: string | null;
  modelProfile: ModelProfile | null;
  modelProfiles: ModelProfile[];
  attendees: Attendee[];
  attendeeCount: number;
  guestList: GuestListItem[];
  imageUrl: string | null;
  imageArtistCredit: string | null;
  imageModelCredit: string | null;
  userRsvpStatus: 'pending' | 'confirmed' | 'declined' | null;
  hasTicket: boolean;
  hasCompletedTicketPayment?: boolean;
  ticketAccessDebug?: {
    isConfirmedArtistInvite: boolean;
    hasCompletedTicketPayment: boolean;
    matchedInviteId: string | null;
    matchedInviteRole: string | null;
    matchedInviteRsvpStatus: string | null;
    matchedInviteBlockedAt: string | null;
    matchedByToken: boolean;
  };
  confirmedCount: number;
  ticketCount: number;
  maxAttendees: number | null;
  isSoldOut: boolean;
  spotsRemaining: number | null;
  // Pricing
  pricingType: 'free' | 'fixed' | 'donation';
  currency?: string; // ISO 4217 event currency (USD when absent)
  ticketPrice: number | null; // In cents
  suggestedMinimum: number | null; // In cents for donation
  userInviteId: string | null; // User's invite ID for checkout
  modelInvite: ModelInviteSummary | null;
  modelExpectations: string | null;
  revenueSplitMode: RevenueSplitMode;
  splitRecipientCount: number;
  modelSplitPercent: number;
  modelGuarantee: number | null;
}

interface GuestListItem {
  id: string;
  name: string | null;
  email: string | null;
  rsvpStatus: 'pending' | 'confirmed' | 'declined';
  blockedAt: string | null;
  blockedByProfileId: string | null;
  blockReason: string | null;
  photoUrl: string | null;
  profileId: string | null;
  paymentId: string | null;
  paymentStatus: string | null;
  amountPaid: number;
  refundedAmount: number;
  refundableAmount: number;
  refundRequestStatus: 'pending' | 'handled' | null;
  currency: string | null;
}

interface ModelInviteSummary {
  id: string;
  name: string | null;
  email: string | null;
  rsvpStatus: 'pending' | 'confirmed' | 'declined';
  inviteToken: string | null;
}

interface PreSessionStatus {
  hasStarted: boolean;
  isClosed: boolean;
  scheduledAt: string | null;
  isTicketHolder: boolean;
  isWithinPreparationWindow: boolean;
  hasPreparingParticipants: boolean;
  preparingNames: string[];
  shouldShowPreparation: boolean;
  canEnter: boolean;
}

import { currencySymbol, formatMoney, formatMoneyCompact } from '../../shared/money';
import { usePlatformFee } from '../../shared/usePlatformFee';

function formatDollars(dollars: number, currency: string = 'USD') {
  return formatMoneyCompact(Math.round(dollars * 100), currency);
}

function getVisitorPurchaseLabel(invitation: InvitationData) {
  if (invitation.isSoldOut && invitation.userRsvpStatus !== 'confirmed') {
    return 'Sold Out';
  }

  if (invitation.pricingType === 'free') {
    return 'Reserve for Free';
  }

  if (invitation.pricingType === 'donation') {
    return 'Pay & Reserve';
  }

  return `Buy Ticket · ${formatMoney(invitation.ticketPrice || 0, invitation.currency)}`;
}

function hasSeparateModelForPayment(invitation: InvitationData) {
  if (invitation.modelOption === 'none' || invitation.modelOption === 'self') return false;

  const hostProfileId = invitation.hostProfile?.id ?? invitation.hostProfileId;
  const modelProfiles = invitation.modelProfiles?.length
    ? invitation.modelProfiles
    : invitation.modelProfile
      ? [invitation.modelProfile]
      : [];
  const hasSeparateModelProfile = modelProfiles.some(profile => !hostProfileId || profile.id !== hostProfileId);

  if (modelProfiles.length > 0 && !hasSeparateModelProfile) return false;

  return Boolean(
    hasSeparateModelProfile ||
    (invitation.modelInvite && invitation.modelInvite.rsvpStatus !== 'declined')
  );
}

function isWithinPreSessionPollingWindow(scheduledAt: string | null) {
  if (!scheduledAt) return false;

  const startTime = new Date(scheduledAt).getTime();
  if (!Number.isFinite(startTime)) return false;

  return Date.now() >= startTime - 60 * 60 * 1000;
}

function formatNameList(names: string[]) {
  const cleanedNames = Array.from(new Set(
    names
      .map((name) => name.trim())
      .filter(Boolean)
  ));

  if (cleanedNames.length === 0) return 'The organizer';
  if (cleanedNames.length === 1) return cleanedNames[0];
  if (cleanedNames.length === 2) return `${cleanedNames[0]} and ${cleanedNames[1]}`;

  return `${cleanedNames.slice(0, -1).join(', ')}, and ${cleanedNames[cleanedNames.length - 1]}`;
}

function playRoomOpenSound() {
  try {
    const AudioContextCtor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) return;

    const audioContext = new AudioContextCtor();
    const oscillator = audioContext.createOscillator();
    const gain = audioContext.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(660, audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(990, audioContext.currentTime + 0.18);
    gain.gain.setValueAtTime(0.0001, audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.12, audioContext.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.45);
    oscillator.connect(gain);
    gain.connect(audioContext.destination);
    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.5);
    window.setTimeout(() => audioContext.close().catch(() => undefined), 700);
  } catch {
    // Browser autoplay rules can block this until the page has received user interaction.
  }
}

function showRoomOpenNotification() {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;

  try {
    new Notification('The room is open', {
      body: 'Enter the session when you are ready.',
      tag: 'drawclub-room-open',
    });
  } catch {
    // Notification support varies by browser and platform.
  }
}

function DisabledVisitorPurchaseButton({
  invitation,
  tooltip,
}: {
  invitation: InvitationData;
  tooltip?: string;
}) {
  const isSoldOut = invitation.isSoldOut && invitation.userRsvpStatus !== 'confirmed';

  return (
    <div className="group relative w-full" title={tooltip}>
      <button
        type="button"
        disabled
        className={`btn btn-lg w-full justify-center cursor-not-allowed ${
          isSoldOut
            ? 'bg-panel2 text-text-dim border border-line'
            : 'btn-accent opacity-70'
        }`}
      >
        {getVisitorPurchaseLabel(invitation)}
        {!isSoldOut && <span aria-hidden="true">→</span>}
      </button>
      {tooltip && (
        <div className="pointer-events-none absolute bottom-[calc(100%+0.5rem)] left-1/2 z-20 w-max max-w-[min(18rem,calc(100vw-2rem))] -translate-x-1/2 rounded-md bg-text px-3 py-2 text-center text-[12px] font-medium leading-snug text-bg opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
          {tooltip}
        </div>
      )}
    </div>
  );
}

function formatCountryName(country: string | null) {
  if (!country) return null;
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(country) ?? country;
  } catch {
    return country;
  }
}

function EventModelProfileCard({ profile }: { profile: ModelProfile }) {
  const profileUrl = `/u/${profile.username || profile.id}`;
  const countryName = formatCountryName(profile.country);

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:gap-6">
      <Link
        to={profileUrl}
        className="aspect-[4/5] w-full overflow-hidden rounded-xl bg-panel2 transition-opacity hover:opacity-90 sm:h-52 sm:w-40 sm:flex-shrink-0"
      >
        {profile.photos?.[0] ? (
          <img
            src={profile.photos[0].url}
            alt={profile.displayName}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center font-display text-[56px] text-accent">
            {profile.displayName.charAt(0)}
          </div>
        )}
      </Link>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center justify-between gap-3">
          <Link
            to={profileUrl}
            className="font-display text-xl font-normal transition-colors hover:text-accent"
          >
            {profile.displayName}
          </Link>
          <FollowButton
            profileId={profile.id}
            followingLabel="Following"
            followLabel="Follow"
          />
        </div>

        {(countryName || profile.instagram) && (
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[13px] text-text-dim">
            {countryName && <span>{countryName}</span>}
            {countryName && profile.instagram && <span className="text-text-faint">·</span>}
            {profile.instagram && (
              <a
                href={`https://instagram.com/${profile.instagram}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-text-dim transition-colors hover:text-text"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="opacity-70" aria-hidden="true">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                {profile.instagram}
              </a>
            )}
          </div>
        )}

        {profile.bio && (
          <p className="mt-2 flex-1 text-[13px] leading-relaxed text-text-dim">
            {profile.bio}
          </p>
        )}
      </div>
    </div>
  );
}

function PreparingSessionScreen({
  eventName,
  names,
}: {
  eventName: string;
  names: string[];
}) {
  const nameLabel = formatNameList(names);
  const verb = names.length === 1 ? 'is' : 'are';

  return (
    <div className="min-h-screen bg-bg pt-[72px]">
      <NavBar
        variant="light"
        links={[
          { label: 'Drawing Events', href: '/events' },
          { label: 'Artists', href: '/artists' },
          { label: 'Models', href: '/models' },
        ]}
      />

      <main className="flex min-h-[calc(100vh-72px)] items-center justify-center px-6 py-12">
        <section className="w-full max-w-xl text-center">
          <div className="mx-auto mb-8 flex h-28 w-28 items-center justify-center rounded-full border border-accent/25 bg-accent-soft/60">
            <div className="relative h-20 w-20">
              <div className="absolute inset-0 rounded-full border border-accent/30 animate-ping" />
              <div className="absolute inset-3 rounded-full border border-accent/45" />
              <div className="absolute left-1/2 top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent shadow-[0_0_32px_rgba(165,95,62,0.45)]" />
            </div>
          </div>

          <p className="font-mono text-[12px] font-semibold uppercase tracking-[0.16em] text-text-faint">
            {eventName}
          </p>
          <h1 className="mt-4 font-display text-[clamp(34px,7vw,56px)] font-light leading-[1.04] text-text">
            {nameLabel} {verb} preparing the session. The room will open shortly.
          </h1>
        </section>
      </main>
    </div>
  );
}

function WaitingSessionScreen({
  eventName,
  scheduledAt,
  onBackToEvent,
}: {
  eventName: string;
  scheduledAt: string | null;
  onBackToEvent: () => void;
}) {
  const targetDate = scheduledAt ? new Date(scheduledAt) : null;
  const hasCountdown = Boolean(targetDate && Number.isFinite(targetDate.getTime()) && targetDate.getTime() > Date.now());

  return (
    <div className="min-h-screen bg-bg pt-[72px]">
      <NavBar
        variant="light"
        links={[
          { label: 'Drawing Events', href: '/events' },
          { label: 'Artists', href: '/artists' },
          { label: 'Models', href: '/models' },
        ]}
      />

      <main className="flex min-h-[calc(100vh-72px)] items-center justify-center px-6 py-12">
        <section className="w-full max-w-xl text-center">
          <p className="font-mono text-[12px] font-semibold uppercase tracking-[0.16em] text-text-faint">
            {eventName}
          </p>
          <h1 className="mt-4 font-display text-[clamp(34px,7vw,56px)] font-light leading-[1.04] text-text">
            Waiting for the room to open
          </h1>
          <p className="mx-auto mt-5 max-w-md text-[15px] leading-relaxed text-text-dim">
            The session exists, but the organizer has not opened the room yet.
          </p>

          {hasCountdown && targetDate && (
            <div className="mx-auto mt-8 inline-flex rounded-xl bg-black/80 px-4 py-3 shadow-md">
              <Countdown targetDate={targetDate} />
            </div>
          )}

          <button
            type="button"
            onClick={onBackToEvent}
            className="btn mt-8 justify-center"
          >
            Back to Event
          </button>
        </section>
      </main>
    </div>
  );
}

function RoomOpenModal({
  joining,
  onEnter,
}: {
  joining: boolean;
  onEnter: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4 py-6">
      <div
        className="w-full max-w-md rounded-xl border border-line bg-panel p-6 text-center shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="room-open-modal-title"
      >
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-accent-soft text-accent">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </div>
        <h2 id="room-open-modal-title" className="font-display text-[32px] leading-tight text-text">
          The room is open
        </h2>
        <button
          type="button"
          onClick={onEnter}
          disabled={joining}
          className="btn btn-accent btn-lg mt-6 w-full justify-center disabled:cursor-not-allowed disabled:opacity-60"
        >
          {joining ? 'Entering...' : 'Enter'}
        </button>
      </div>
    </div>
  );
}

function ModelPrepModal({
  joining,
  onClose,
  onEnter,
}: {
  joining: boolean;
  onClose: () => void;
  onEnter: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4 py-6">
      <div
        className="w-full max-w-md rounded-xl border border-line bg-panel p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="model-prep-modal-title"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-text-faint">
              Model confirmed
            </p>
            <h2 id="model-prep-modal-title" className="mt-2 font-display text-[32px] leading-tight text-text">
              Prepare the session
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-line text-text-dim transition-colors hover:border-text/30 hover:text-text"
            aria-label="Close preparation modal"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="mt-3 type-body-sm text-text-dim">
          You can enter the room now to create your pose schedule and get set up before artists arrive.
        </p>

        <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={onClose}
            className="btn justify-center"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={onEnter}
            disabled={joining}
            className="btn btn-accent justify-center disabled:cursor-not-allowed disabled:opacity-60"
          >
            {joining ? 'Entering...' : 'Enter Room'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Countdown({ targetDate }: { targetDate: Date }) {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  function calculateTimeLeft() {
    const now = new Date().getTime();
    const target = targetDate.getTime();
    const diff = Math.max(0, target - now);

    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((diff % (1000 * 60)) / 1000),
    };
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  const pad = (n: number) => String(n).padStart(2, '0');

  return (
    <div className="flex items-center gap-1 text-white">
      <div className="flex flex-col items-center w-9">
        <span className="font-display text-[24px] font-normal leading-none tabular-nums">{pad(timeLeft.days)}</span>
        <span className="font-mono text-[8px] text-white/60 uppercase mt-0.5">days</span>
      </div>
      <span className="font-display text-[18px] font-light text-white/40 pb-3">:</span>
      <div className="flex flex-col items-center w-9">
        <span className="font-display text-[24px] font-normal leading-none tabular-nums">{pad(timeLeft.hours)}</span>
        <span className="font-mono text-[8px] text-white/60 uppercase mt-0.5">hrs</span>
      </div>
      <span className="font-display text-[18px] font-light text-white/40 pb-3">:</span>
      <div className="flex flex-col items-center w-9">
        <span className="font-display text-[24px] font-normal leading-none tabular-nums">{pad(timeLeft.minutes)}</span>
        <span className="font-mono text-[8px] text-white/60 uppercase mt-0.5">min</span>
      </div>
      <span className="font-display text-[18px] font-light text-white/40 pb-3">:</span>
      <div className="flex flex-col items-center w-9">
        <span className="font-display text-[24px] font-normal leading-none tabular-nums">{pad(timeLeft.seconds)}</span>
        <span className="font-mono text-[8px] text-white/60 uppercase mt-0.5">sec</span>
      </div>
    </div>
  );
}

function AttendeeGrid({ attendees }: { attendees: Attendee[] }) {
  const colors = ['#a55f3e', '#6e7a58', '#94533a', '#607a64', '#a55f3e'];

  return (
    <div className="flex flex-wrap gap-3">
      {attendees.map((att, i) => (
        <div key={i} className="flex items-center gap-2.5 bg-panel2/60 rounded-full pl-1 pr-3 py-1">
          {att.photoUrl ? (
            <img
              src={att.photoUrl}
              alt={att.name || 'Attendee'}
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <div
              className="w-8 h-8 rounded-full bg-panel border border-line flex items-center justify-center font-display text-[13px]"
              style={{ color: colors[i % colors.length] }}
            >
              {att.initial}
            </div>
          )}
          <span className="text-[13px] text-text font-medium">
            {att.name || 'Guest'}
          </span>
        </div>
      ))}
    </div>
  );
}

function GuestList({
  guests,
  currency = 'USD',
  canManage = false,
  onRefund,
  onBlock,
  onUnblock,
  busyGuestId,
}: {
  guests: GuestListItem[];
  currency?: string;
  canManage?: boolean;
  onRefund?: (guest: GuestListItem) => void;
  onBlock?: (guest: GuestListItem) => void;
  onUnblock?: (guest: GuestListItem) => void;
  busyGuestId?: string | null;
}) {
  const visibleGuests = guests.filter((guest) => guest.rsvpStatus === 'confirmed' || guest.blockedAt || guest.amountPaid > 0);

  if (visibleGuests.length === 0) {
    return <p className="text-[13px] text-text-dim">No guests yet.</p>;
  }

  const paymentLabel = (guest: GuestListItem) => {
    if (guest.refundRequestStatus === 'pending') return `${formatMoney(guest.amountPaid, currency)} paid · refund requested`;
    if (guest.refundRequestStatus === 'handled') return `${formatMoney(guest.amountPaid, currency)} paid · refund request resolved`;
    if (guest.amountPaid <= 0) return 'No payment';
    if (guest.refundedAmount >= guest.amountPaid) return `${formatMoney(guest.amountPaid, currency)} refunded`;
    if (guest.refundedAmount > 0) return `${formatMoney(guest.refundedAmount, currency)} of ${formatMoney(guest.amountPaid, currency)} refunded`;
    return `${formatMoney(guest.amountPaid, currency)} paid`;
  };

  return (
    <div className="divide-y divide-line-soft">
      {visibleGuests.map((guest) => {
        const isBusy = busyGuestId === guest.id;
        const guestLabel = guest.name || 'Guest';
        return (
          <div key={guest.id} className="flex items-center justify-between gap-3 py-2 text-[13px]">
            <div className="min-w-0 flex-1">
              <div className="truncate text-text">{guestLabel}</div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px] text-text-dim">
                <span>{paymentLabel(guest)}</span>
                {guest.blockedAt && (
                  <span className="text-warn">Blocked</span>
                )}
              </div>
            </div>
            {canManage && (
              <div className="flex shrink-0 items-center justify-end gap-2">
                {guest.refundableAmount > 0 && !guest.refundRequestStatus && (
                  <button
                    type="button"
                    onClick={() => onRefund?.(guest)}
                    disabled={isBusy}
                    className="btn btn-sm disabled:opacity-50"
                  >
                    Refund
                  </button>
                )}
                {guest.blockedAt ? (
                  <button
                    type="button"
                    onClick={() => onUnblock?.(guest)}
                    disabled={isBusy}
                    className="btn btn-sm disabled:opacity-50"
                  >
                    {isBusy ? 'Saving...' : 'Unblock'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => onBlock?.(guest)}
                    disabled={isBusy}
                    className="btn btn-sm disabled:opacity-50"
                  >
                    {isBusy ? 'Saving...' : 'Block'}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface TicketRefundRequest {
  id: string;
  status: 'pending' | 'handled';
  requesterName: string | null;
  buyerEmail: string | null;
  amountCents: number | null;
  currency: string | null;
  requestedAt: string;
  resolutionOutcome: string | null;
}

/**
 * Organizer queue of artist ticket-cancellation refund requests for this
 * event. Refunds are never automatic: the organizer approves (full Stripe
 * refund, fees included) or declines each request here. Renders nothing when
 * there is no pending request to decide.
 */
function RefundRequestsPanel({
  roomId,
  getToken,
  onResolved,
}: {
  roomId: string;
  getToken: () => Promise<string | null>;
  onResolved: () => Promise<void>;
}) {
  const [requests, setRequests] = useState<TicketRefundRequest[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [decliningId, setDecliningId] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const loadRequests = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`/api/stripe/events/${roomId}/refund-requests`, {
        headers: authHeaders(token),
      });
      const data = await response.json();
      if (response.ok) {
        setRequests(data.refundRequests || []);
      }
    } catch (err) {
      console.error('Error loading refund requests:', err);
    }
  };

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  const resolveRequest = async (request: TicketRefundRequest, resolution: 'approve' | 'decline', note: string | null) => {
    if (busyId) return;

    const amountLabel = request.amountCents !== null
      ? formatMoney(request.amountCents, request.currency)
      : 'this ticket';
    if (resolution === 'approve' && !window.confirm(
      `Refund ${amountLabel} to ${request.requesterName || request.buyerEmail || 'this attendee'}? The full amount, including any fees, is returned to their card.`
    )) {
      return;
    }

    setBusyId(request.id);
    setError(null);
    setNotice(null);
    try {
      const token = await getToken();
      const response = await fetch(`/api/stripe/events/${roomId}/refund-requests/${request.id}/${resolution}`, {
        method: 'POST',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({ resolutionNote: note }),
      });
      const data = await response.json();
      if (!response.ok) {
        const authMessage = signInMessageForAuthStatus(response, 'Sign in to manage refund requests');
        if (authMessage) throw new Error(authMessage);
        throw new Error(data.error || 'Failed to resolve refund request');
      }
      setNotice(resolution === 'approve' ? 'Refund sent. The full amount is on its way back to their card.' : 'Refund request declined.');
      setDecliningId(null);
      setDeclineReason('');
      await loadRequests();
      await onResolved();
    } catch (err) {
      console.error('Error resolving refund request:', err);
      setError(err instanceof Error ? err.message : 'Failed to resolve refund request');
    } finally {
      setBusyId(null);
    }
  };

  const pendingRequests = requests.filter((request) => request.status === 'pending');
  if (pendingRequests.length === 0 && !notice && !error) return null;

  return (
    <SidePanelSection title="Refund requests">
      <div className="space-y-2" data-testid="refund-requests-panel">
        {notice && (
          <div className="rounded-lg border border-ok/25 bg-ok/10 p-3 text-[13px] text-text">{notice}</div>
        )}
        {error && (
          <div role="alert" className="rounded-lg border border-warn/30 bg-warn/10 p-3 text-[13px] text-warn">{error}</div>
        )}
        {pendingRequests.length === 0 && !error && (
          <p className="text-[13px] text-text-dim">No pending refund requests.</p>
        )}
        {pendingRequests.map((request) => {
          const isBusy = busyId === request.id;
          return (
            <div key={request.id} className="rounded-lg border border-line-soft bg-panel2/40 p-3 text-[13px]">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-text">{request.requesterName || request.buyerEmail || 'Attendee'}</div>
                  {request.buyerEmail && <div className="truncate text-[11.5px] text-text-faint">{request.buyerEmail}</div>}
                </div>
                <span className="whitespace-nowrap text-text">
                  {request.amountCents !== null ? formatMoney(request.amountCents, request.currency) : 'Amount unknown'}
                </span>
              </div>
              <p className="mt-1.5 text-[11.5px] text-text-dim">
                Cancelled their ticket and asked for a refund. Nothing is refunded until you decide.
              </p>
              {decliningId === request.id ? (
                <div className="mt-3 space-y-2">
                  <input
                    type="text"
                    value={declineReason}
                    onChange={(event) => setDeclineReason(event.target.value)}
                    placeholder="Reason (optional)"
                    className="w-full rounded-md border border-line bg-panel px-2.5 py-1.5 text-[13px] text-text placeholder:text-text-faint"
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => resolveRequest(request, 'decline', declineReason.trim() || null)}
                      disabled={isBusy}
                      className="btn btn-sm text-warn border-warn/40 hover:border-warn disabled:opacity-50"
                    >
                      {isBusy ? 'Saving...' : 'Confirm decline'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDecliningId(null);
                        setDeclineReason('');
                      }}
                      disabled={isBusy}
                      className="btn btn-sm disabled:opacity-50"
                    >
                      Keep deciding
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => resolveRequest(request, 'approve', null)}
                    disabled={isBusy}
                    className="btn btn-sm disabled:opacity-50"
                  >
                    {isBusy ? 'Refunding...' : 'Approve refund'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDecliningId(request.id);
                      setDeclineReason('');
                    }}
                    disabled={isBusy}
                    className="btn btn-sm disabled:opacity-50"
                  >
                    Decline
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </SidePanelSection>
  );
}

function InvitationsPanel({
  invitations,
  roomCode,
  getToken,
  onRefresh,
}: {
  invitations: GuestListItem[];
  roomCode: string;
  getToken: () => Promise<string | null>;
  onRefresh: () => Promise<void>;
}) {
  const [emailInput, setEmailInput] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isNotifyingFollowers, setIsNotifyingFollowers] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const parseEmails = () => Array.from(new Set(
    emailInput
      .split(/[\s,;]+/)
      .map((email) => email.trim().toLowerCase())
      .filter((email) => email.length > 0)
  ));

  const handleAddInvites = async () => {
    const emails = parseEmails();
    if (emails.length === 0 || isAdding) return;

    setIsAdding(true);
    setStatusMessage(null);
    let created = 0;
    let skipped = 0;
    let failed = 0;

    try {
      const token = await getToken();
      for (const email of emails) {
        const response = await fetch(`/api/rooms/${roomCode}/invites`, {
          method: 'POST',
          headers: jsonAuthHeaders(token),
          body: JSON.stringify({ email }),
        });

        if (!response.ok) {
          failed += 1;
          continue;
        }

        const data = await response.json();
        if (data.created === false) {
          skipped += 1;
        } else {
          created += 1;
        }
      }

      setEmailInput('');
      setStatusMessage([
        created ? `${created} invited` : null,
        skipped ? `${skipped} already invited` : null,
        failed ? `${failed} failed` : null,
      ].filter(Boolean).join(', ') || 'No new invitations sent');
      await onRefresh();
    } catch (error) {
      console.error('Error adding invitations:', error);
      setStatusMessage('Failed to send invitations');
    } finally {
      setIsAdding(false);
    }
  };

  const handleNotifyFollowers = async () => {
    if (isNotifyingFollowers) return;

    setIsNotifyingFollowers(true);
    setStatusMessage(null);

    try {
      const token = await getToken();
      const response = await fetch(`/api/rooms/${roomCode}/invites/followers`, {
        method: 'POST',
        headers: authHeaders(token),
      });

      if (!response.ok) {
        throw new Error('Failed to notify followers');
      }

      const data = await response.json();
      setStatusMessage([
        data.created ? `${data.created} followers invited` : null,
        data.skipped ? `${data.skipped} already invited` : null,
        data.emailed ? `${data.emailed} emailed` : null,
      ].filter(Boolean).join(', ') || 'No new followers to notify');
      await onRefresh();
    } catch (error) {
      console.error('Error notifying followers:', error);
      setStatusMessage('Failed to notify followers');
    } finally {
      setIsNotifyingFollowers(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <textarea
          value={emailInput}
          onChange={(event) => setEmailInput(event.target.value)}
          placeholder="Email addresses separated by commas"
          rows={3}
          className="w-full resize-none rounded-lg border border-line bg-panel px-3 py-2 text-[13px] text-text outline-none placeholder:text-text-faint focus:border-text/40"
        />
        <button
          type="button"
          onClick={handleAddInvites}
          disabled={isAdding || parseEmails().length === 0}
          className="btn btn-accent w-full justify-center disabled:opacity-50"
        >
          {isAdding ? 'Sending...' : 'Send invitations'}
        </button>
      </div>

      <button
        type="button"
        onClick={handleNotifyFollowers}
        disabled={isNotifyingFollowers}
        className="btn w-full justify-center disabled:opacity-50"
      >
        {isNotifyingFollowers ? 'Notifying...' : 'Notify my followers'}
      </button>

      {statusMessage && (
        <p className="text-[12px] text-text-dim">{statusMessage}</p>
      )}

      {invitations.length > 0 && (
        <div className="space-y-2 pt-2">
          {invitations.slice(0, 8).map((invite) => (
            <div key={invite.id} className="flex items-center justify-between gap-3 text-[13px]">
              <div className="min-w-0">
                <div className="truncate text-text">{invite.name || invite.email || 'Guest'}</div>
                {invite.email && <div className="truncate text-[11.5px] text-text-faint">{invite.email}</div>}
              </div>
              <span className="text-[11px] capitalize text-text-dim">
                {invite.rsvpStatus === 'pending' ? 'Invited' : invite.rsvpStatus}
              </span>
            </div>
          ))}
          {invitations.length > 8 && (
            <p className="text-[12px] text-text-faint">+{invitations.length - 8} more</p>
          )}
        </div>
      )}
    </div>
  );
}

function SidePanelSection({
  title,
  titleVariant = 'label',
  children,
}: {
  title: string;
  titleVariant?: 'label' | 'medium';
  children: ReactNode;
}) {
  const titleClassName = titleVariant === 'medium'
    ? 'font-display text-[24px] font-light text-text leading-[1.08] mb-4'
    : 'font-mono text-[12px] text-text-faint uppercase font-semibold mb-3';

  return (
    <section className="border-t border-line-soft pt-5 first:border-t-0 first:pt-0">
      <h2 className={titleClassName}>{title}</h2>
      {children}
    </section>
  );
}

function ShareLinkField({
  url,
  copied,
  onCopy,
}: {
  url: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div className="rounded-lg border border-line-soft bg-bg-secondary p-3">
      <div className="text-[11px] text-text-dim mb-2">Share link</div>
      <div className="flex items-stretch gap-2">
        <input
          readOnly
          value={url}
          onFocus={(event) => event.currentTarget.select()}
          className="min-w-0 flex-1 rounded-md border border-line bg-panel px-2.5 py-2 text-[12px] text-text outline-none"
        />
        <button
          type="button"
          onClick={onCopy}
          className="rounded-md border border-line bg-panel px-3 text-[12px] font-medium text-text hover:border-text/30 hover:bg-panel-secondary transition-colors"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

function CompactPayoutSimulator({
  invitation,
  role,
  feePercent,
  value,
  onChange,
}: {
  invitation: InvitationData;
  role: 'host' | 'model';
  feePercent: number;
  value: number;
  onChange: (value: number) => void;
}) {
  const maxTickets = Math.max(1, invitation.maxAttendees ?? 50);
  const seatCount = Math.min(Math.max(0, value), maxTickets);
  const perAttendeeCents = invitation.ticketPrice ?? invitation.suggestedMinimum ?? 0;
  const perAttendeeDollars = perAttendeeCents / 100;
  const grossRevenueCents = perAttendeeCents * seatCount;
  const platformFeeCents = Math.round(grossRevenueCents * (feePercent / 100));
  const netRevenueCents = grossRevenueCents - platformFeeCents;
  const isEvenSplit = invitation.revenueSplitMode === 'even';
  const splitRecipientCount = Math.max(1, invitation.splitRecipientCount || 1);
  const splitPayoutCents = isEvenSplit
    ? Math.floor(netRevenueCents / splitRecipientCount)
    : Math.round(netRevenueCents * (invitation.modelSplitPercent / 100));
  const guaranteeCents = invitation.modelGuarantee ?? 0;
  const guaranteeTopUpCents = Math.max(0, guaranteeCents - splitPayoutCents);
  const modelPayoutCents = splitPayoutCents + guaranteeTopUpCents;
  const organizerPayoutCents = isEvenSplit
    ? Math.max(0, netRevenueCents - modelPayoutCents - (splitPayoutCents * Math.max(0, splitRecipientCount - 2)))
    : Math.max(0, netRevenueCents - modelPayoutCents);
  const grossRevenue = grossRevenueCents / 100;
  const platformFee = platformFeeCents / 100;
  const modelPayout = modelPayoutCents / 100;
  const organizerPayout = organizerPayoutCents / 100;
  const guaranteeTopUp = guaranteeTopUpCents / 100;
  const hasPerAttendeeAmount = perAttendeeCents > 0;
  const amountLabel = invitation.pricingType === 'donation' ? 'suggested donation' : 'ticket';

  return (
    <div className="mt-4 rounded-lg border border-line bg-panel p-3 shadow-sm">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="font-mono text-[10px] font-semibold text-text-dim uppercase">
          Payout simulator
        </div>
        <div className="text-[12px] font-medium text-text tabular-nums">
          {seatCount} seat{seatCount === 1 ? '' : 's'}
        </div>
      </div>

      <input
        type="range"
        min="0"
        max={maxTickets}
        value={seatCount}
        disabled={!hasPerAttendeeAmount}
        onChange={(event) => onChange(Number(event.target.value))}
        className="w-full h-2 bg-line rounded-full appearance-none cursor-pointer disabled:cursor-not-allowed disabled:opacity-40
          [&::-webkit-slider-thumb]:appearance-none
          [&::-webkit-slider-thumb]:w-4
          [&::-webkit-slider-thumb]:h-4
          [&::-webkit-slider-thumb]:rounded-full
          [&::-webkit-slider-thumb]:bg-accent
          [&::-webkit-slider-thumb]:cursor-pointer
          [&::-moz-range-thumb]:w-4
          [&::-moz-range-thumb]:h-4
          [&::-moz-range-thumb]:rounded-full
          [&::-moz-range-thumb]:bg-accent
          [&::-moz-range-thumb]:border-0
          [&::-moz-range-thumb]:cursor-pointer"
      />
      <div className="mt-2 text-[11.5px] text-text-dim">
        Using {formatDollars(perAttendeeDollars, invitation.currency)} per {amountLabel}.
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="rounded-md bg-bg-secondary p-2.5">
          <div className="text-[10px] text-text-dim uppercase">Gross</div>
          <div className="mt-0.5 text-[15px] font-semibold text-text tabular-nums">
            {formatDollars(grossRevenue, invitation.currency)}
          </div>
        </div>
        <div className="rounded-md bg-bg-secondary p-2.5">
          <div className="text-[10px] text-text-dim uppercase">Fee</div>
          <div className="mt-0.5 text-[15px] font-semibold text-warn tabular-nums">
            -{formatDollars(platformFee, invitation.currency)}
          </div>
        </div>
      </div>

      {role === 'host' ? (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="rounded-md border border-accent/20 bg-accent/10 p-3">
            <div className="text-[10px] font-medium text-accent uppercase">Organizer</div>
            <div className="font-display text-[22px] leading-none text-accent tabular-nums">
              {formatDollars(organizerPayout, invitation.currency)}
            </div>
            <div className="mt-1 text-[10.5px] text-text">
              {isEvenSplit ? 'Even share' : `${100 - invitation.modelSplitPercent}% of net`}
            </div>
          </div>
          <div className="rounded-md border border-tag/20 bg-tag/10 p-3">
            <div className="text-[10px] font-medium text-tag uppercase">Model</div>
            <div className="font-display text-[22px] leading-none text-tag tabular-nums">
              {formatDollars(modelPayout, invitation.currency)}
            </div>
            <div className="mt-1 text-[10.5px] text-text">
              {isEvenSplit ? 'Even share' : `${invitation.modelSplitPercent}% of net`}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-3 rounded-md border border-tag/20 bg-tag/10 p-3">
          <div className="flex items-end justify-between gap-3">
            <div>
              <div className="text-[10px] font-medium text-tag uppercase">You receive</div>
              <div className="font-display text-[24px] leading-none text-tag tabular-nums">
                {formatDollars(modelPayout, invitation.currency)}
              </div>
            </div>
            <div className="pb-0.5 text-right text-[11px] text-text">
              {isEvenSplit ? 'Even share' : `${invitation.modelSplitPercent}% of net`}
            </div>
          </div>
        </div>
      )}

      {guaranteeTopUp > 0 && (
        <div className="mt-2 text-[11.5px] text-text">
          Includes {formatDollars(guaranteeTopUp, invitation.currency)} guarantee top-up for the model.
        </div>
      )}
      {!hasPerAttendeeAmount && (
        <div className="mt-2 text-[11.5px] text-text">
          No fixed attendee amount yet, so this shows the guarantee.
        </div>
      )}
    </div>
  );
}

export function InvitationPage() {
  const { code } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken, isSignedIn } = useAuth();
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [rsvping, setRsvping] = useState(false);
  const [copied, setCopied] = useState(false);
  const [donationAmount, setDonationAmount] = useState<string>('');
  const [modelResponding, setModelResponding] = useState(false);
  const [payoutSeatCount, setPayoutSeatCount] = useState(10);
  const [showPayoutSimulator, setShowPayoutSimulator] = useState(false);
  const [busyGuestId, setBusyGuestId] = useState<string | null>(null);
  const [refundGuest, setRefundGuest] = useState<GuestListItem | null>(null);
  const [refundMode, setRefundMode] = useState<'full' | 'half' | 'custom'>('full');
  const [customRefundAmount, setCustomRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [preSessionStatus, setPreSessionStatus] = useState<PreSessionStatus | null>(null);
  const platformFeeInfo = usePlatformFee();
  const [waitingForSessionOpen, setWaitingForSessionOpen] = useState(false);
  const [showRoomOpenModal, setShowRoomOpenModal] = useState(false);
  const [showModelPrepModal, setShowModelPrepModal] = useState(false);
  const [showCancelEventModal, setShowCancelEventModal] = useState(false);
  const [cancelEventReason, setCancelEventReason] = useState('');
  const [cancellingEvent, setCancellingEvent] = useState(false);
  const [cancelEventError, setCancelEventError] = useState<string | null>(null);
  const previousRoomOpenRef = useRef<boolean | null>(null);
  const modelPrepModalShownRef = useRef(false);
  const originalTitleRef = useRef<string | null>(null);
  const titleAlertIntervalRef = useRef<number | null>(null);

  const urlToken = searchParams.get('token');
  const refundRequestRecorded = searchParams.get('refund') === 'requested';

  useEffect(() => {
    if (code) {
      fetchInvitation();
    }
  }, [code, urlToken]);


  const fetchInvitation = async () => {
    try {
      const token = await getToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const url = urlToken
        ? `/api/rooms/${code}/invitation?token=${encodeURIComponent(urlToken)}`
        : `/api/rooms/${code}/invitation`;
      const response = await fetch(url, { headers });
      if (!response.ok) {
        if (response.status === 404) {
          setError('Invitation not found');
        } else {
          setError('Failed to load invitation');
        }
        return;
      }
      const data = await response.json();
      setInvitation(data);
      console.info('[InvitationPage] Loaded event ticket state', {
        code,
        signedIn: Boolean(token),
        pricingType: data.pricingType,
        ticketPrice: data.ticketPrice,
        suggestedMinimum: data.suggestedMinimum,
        isHost: data.isHost,
        isModel: data.isModel,
        viewerRole: data.viewerRole,
        isInvited: data.isInvited,
        userInviteId: data.userInviteId,
        userRsvpStatus: data.userRsvpStatus,
        hasTicket: data.hasTicket,
        hasCompletedTicketPayment: data.hasCompletedTicketPayment,
        ticketAccessDebug: data.ticketAccessDebug,
      });
      if (data.pricingType === 'donation' && data.suggestedMinimum && !donationAmount) {
        setDonationAmount((data.suggestedMinimum / 100).toFixed(0));
      }
    } catch (err) {
      setError('Failed to load invitation');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!invitation) return;

    const canJoin = invitation.hasStarted || invitation.isHost || invitation.isModel;
    const hasConfirmedTicket = invitation.hasTicket && !invitation.isHost && !invitation.isModel;
    console.info('[InvitationPage] Derived event CTA state', {
      code,
      canJoin,
      hasConfirmedTicket,
      hasTicket: invitation.hasTicket,
      userRsvpStatus: invitation.userRsvpStatus,
      hasCompletedTicketPayment: invitation.hasCompletedTicketPayment,
      showRolePanel: invitation.isHost || invitation.isModel,
      isSoldOut: invitation.isSoldOut,
      pricingType: invitation.pricingType,
      primaryCta: invitation.isHost || invitation.isModel
        ? 'role-panel'
        : hasConfirmedTicket
          ? canJoin ? 'join-session' : 'you-are-confirmed'
          : invitation.isSoldOut && invitation.userRsvpStatus !== 'confirmed'
            ? 'sold-out'
            : invitation.pricingType === 'free'
              ? 'reserve'
              : invitation.pricingType === 'donation'
                ? 'pay-and-reserve'
                : 'buy-ticket',
    });
  }, [code, invitation]);

  useEffect(() => {
    if (!urlToken || !invitation || modelPrepModalShownRef.current) return;
    if (!invitation.isModel || invitation.modelInvite?.rsvpStatus !== 'confirmed' || invitation.isClosed) return;

    modelPrepModalShownRef.current = true;
    setShowModelPrepModal(true);
  }, [invitation, urlToken]);

  const stopTitleAlert = () => {
    if (titleAlertIntervalRef.current !== null) {
      window.clearInterval(titleAlertIntervalRef.current);
      titleAlertIntervalRef.current = null;
    }

    if (originalTitleRef.current) {
      document.title = originalTitleRef.current;
      originalTitleRef.current = null;
    }
  };

  const startTitleAlert = () => {
    if (titleAlertIntervalRef.current !== null) return;

    originalTitleRef.current = originalTitleRef.current || document.title;
    let showAlertTitle = false;
    titleAlertIntervalRef.current = window.setInterval(() => {
      showAlertTitle = !showAlertTitle;
      document.title = showAlertTitle ? 'The room is open' : originalTitleRef.current || 'DrawClub';
    }, 1000);
  };

  const notifyRoomOpen = () => {
    setShowRoomOpenModal(true);
    startTitleAlert();
    playRoomOpenSound();
    showRoomOpenNotification();
  };

  useEffect(() => () => stopTitleAlert(), []);

  const shouldPollPreSessionStatus = Boolean(
    code &&
    isSignedIn &&
    invitation &&
    !invitation.isClosed &&
    !invitation.cancelledAt &&
    !invitation.hasStarted &&
    !invitation.isHost &&
    !invitation.isModel &&
    invitation.hasTicket &&
    (waitingForSessionOpen || isWithinPreSessionPollingWindow(invitation.scheduledAt))
  );
  const preSessionPollIntervalMs = preSessionStatus?.hasPreparingParticipants ? 2000 : 30000;

  useEffect(() => {
    if (!shouldPollPreSessionStatus || !code) {
      setPreSessionStatus(null);
      previousRoomOpenRef.current = invitation?.hasStarted ?? null;
      return;
    }

    let cancelled = false;

    const pollPreSessionStatus = async () => {
      try {
        const token = await getToken();

        const response = await fetch(`/api/rooms/${code}/pre-session-status`, {
          headers: authHeaders(token),
        });

        if (!response.ok) return;

        const status = await response.json() as PreSessionStatus;
        if (cancelled) return;

        setPreSessionStatus(status);

        if (status.isClosed) {
          setWaitingForSessionOpen(false);
          setInvitation((current) => current ? { ...current, isClosed: true } : current);
          return;
        }

        const wasOpen = previousRoomOpenRef.current;
        if (status.hasStarted) {
          setWaitingForSessionOpen(false);
          setInvitation((current) => current ? { ...current, hasStarted: true } : current);
          if (wasOpen !== true && status.canEnter) {
            notifyRoomOpen();
          }
        }

        previousRoomOpenRef.current = status.hasStarted;
      } catch (error) {
        console.error('Error polling pre-session status:', error);
      }
    };

    pollPreSessionStatus();
    const interval = window.setInterval(pollPreSessionStatus, preSessionPollIntervalMs);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [code, getToken, invitation?.hasStarted, preSessionPollIntervalMs, shouldPollPreSessionStatus]);

  const handleJoin = async () => {
    if (!code) return;
    if (invitation?.isClosed || invitation?.cancelledAt) return;
    stopTitleAlert();

    if (!isSignedIn) {
      const redirectUrl = encodeURIComponent(`${location.pathname}${location.search}`);
      navigate(`/sign-in?redirect_url=${redirectUrl}`);
      return;
    }

    setJoining(true);
    console.log('[InvitationPage] handleJoin started for room:', code);
    try {
      const token = await getToken();
      logVideoDiagnostic('info', 'auth', { hasAuthToken: Boolean(token), source: 'event-page-join' }, {
        roomCode: code,
        participantId: null,
        participantName: null,
        participantRole: null,
      });
      console.log('[InvitationPage] Got auth token, calling /api/rooms/${code}/join');
      const response = await fetch(`/api/rooms/${code}/join`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      console.log('[InvitationPage] Join response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[InvitationPage] Join failed:', response.status, errorData);

        if (errorData.requiresInviteAcceptance) {
          await fetchInvitation();
          setError(errorData.error || 'Please accept your model invitation before joining.');
          return;
        }

        if (errorData.sessionNotOpen) {
          setError(null);
          setWaitingForSessionOpen(true);
          return;
        }

        throw new Error(errorData.error || 'Failed to join');
      }

      const data = await response.json();
      console.log('[InvitationPage] Join response data:', {
        hasToken: !!data.token,
        hasParticipant: !!data.participant,
        displayName: data.displayName,
      });

      // Check if user needs to wait for approval (uninvited guest)
      if (data.requiresTicket) {
        setError('Please buy a ticket before joining this session.');
        return;
      }

      console.log('[InvitationPage] User can join directly, storing session data');
      console.log('[InvitationPage] isHost from server:', data.isHost);
      storeJoinedRoomSession(code, data);
      logVideoDiagnostic('info', 'join-token-created', {
        source: 'event-page-join',
        participantId: data.participant?.id ?? null,
        role: data.participant?.role ?? null,
        isHost: data.isHost === true,
        isInvited: data.isInvited === true,
      }, getRoomSessionDiagnosticContext(code));

      navigate(`/s/${code}/room`);
    } catch (err) {
      console.error('[InvitationPage] Error joining:', err);
      setError(err instanceof Error ? err.message : 'Failed to join the session');
    } finally {
      setJoining(false);
    }
  };

  const handleRsvp = async () => {
    if (!code || !invitation) return;
    setRsvping(true);
    try {
      const token = await getToken();

      // For paid events, redirect to Stripe Checkout
      if (invitation.pricingType === 'fixed' || invitation.pricingType === 'donation') {
        let amountCents: number;

        if (invitation.pricingType === 'fixed') {
          amountCents = invitation.ticketPrice || 0;
        } else {
          // Donation - use the entered amount (or 0 if empty)
          const parsed = parseFloat(donationAmount);
          amountCents = isNaN(parsed) ? 0 : Math.round(parsed * 100);
        }

        // For $0 donations, skip payment and just RSVP
        if (amountCents === 0) {
          const response = await fetch(`/api/rooms/${code}/rsvp`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || 'Failed to reserve');
          }

          navigate(`/s/${code}/confirmed`);
          return;
        }

        // Create checkout session
        const checkoutResponse = await fetch('/api/stripe/checkout/create-session', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            roomId: invitation.id,
            roomCode: invitation.code,
            roomName: invitation.name,
            inviteId: invitation.userInviteId,
            amountCents,
            isDonation: invitation.pricingType === 'donation',
            successUrl: `${window.location.origin}/s/${code}/confirmed?payment=success&session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: `${window.location.origin}/s/${code}?payment=cancelled`,
          }),
        });

        if (!checkoutResponse.ok) {
          const data = await checkoutResponse.json();
          throw new Error(data.error || 'Failed to create checkout');
        }

        const { url } = await checkoutResponse.json();
        window.location.href = url;
        return;
      }

      // Free events - just RSVP
      const response = await fetch(`/api/rooms/${code}/rsvp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const data = await response.json();
        if (data.error === 'sold_out' || data.error === 'Event is sold out') {
          alert('This session is sold out');
          fetchInvitation(); // Refresh to show sold out state
        } else if (data.error === 'This session has ended') {
          alert('This session has ended and is no longer accepting reservations.');
          fetchInvitation(); // Refresh to show ended state
        } else if (response.status === 404) {
          alert('Room not found. It may have been unpublished or deleted.');
        } else {
          alert(data.error || 'Failed to reserve your spot. Please try again.');
        }
        return;
      }

      navigate(`/s/${code}/confirmed`);
    } catch (err) {
      console.error('Error reserving:', err);
      alert('Failed to reserve your spot. Please check your connection and try again.');
    } finally {
      setRsvping(false);
    }
  };

  const handleModelRespond = async (accept: boolean) => {
    if (!code || !invitation?.modelInvite) return;

    if (accept && !isSignedIn) {
      const redirectUrl = encodeURIComponent(`${location.pathname}${location.search}`);
      navigate(`/sign-in?redirect_url=${redirectUrl}`);
      return;
    }

    setModelResponding(true);

    try {
      const token = await getToken();
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`/api/rooms/${code}/invites/${invitation.modelInvite.id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          rsvpStatus: accept ? 'confirmed' : 'declined',
          inviteToken: urlToken || invitation.modelInvite.inviteToken,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to respond to invitation');
      }

      window.dispatchEvent(new Event('model-invitations:updated'));
      await fetchInvitation();
      if (accept) {
        modelPrepModalShownRef.current = true;
        setShowModelPrepModal(true);
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to respond to invitation');
    } finally {
      setModelResponding(false);
    }
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}/s/${code}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openRefundModal = (guest: GuestListItem) => {
    setRefundGuest(guest);
    setRefundMode('full');
    setCustomRefundAmount((guest.refundableAmount / 100).toFixed(2));
    setRefundReason('');
  };

  const getRefundAmountCents = () => {
    if (!refundGuest) return 0;
    if (refundMode === 'full') return refundGuest.refundableAmount;
    if (refundMode === 'half') return Math.max(1, Math.round(refundGuest.refundableAmount / 2));

    const parsed = Number.parseFloat(customRefundAmount);
    if (!Number.isFinite(parsed)) return 0;
    return Math.round(parsed * 100);
  };

  const submitRefund = async () => {
    if (!code || !refundGuest || busyGuestId) return;

    const amountCents = getRefundAmountCents();
    if (amountCents <= 0 || amountCents > refundGuest.refundableAmount) {
      alert(`Choose a refund amount up to ${formatMoney(refundGuest.refundableAmount, invitation?.currency)}.`);
      return;
    }

    setBusyGuestId(refundGuest.id);
    try {
      const token = await getToken();
      const response = await fetch(`/api/rooms/${code}/guests/${refundGuest.id}/refund`, {
        method: 'POST',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({
          amountCents,
          reason: refundReason.trim() || null,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const authMessage = signInMessageForAuthStatus(response, 'Sign in to refund guests');
        if (authMessage) throw new Error(authMessage);
        throw new Error(data.error || 'Failed to refund guest');
      }

      setRefundGuest(null);
      await fetchInvitation();
    } catch (err) {
      console.error('Error refunding guest:', err);
      alert(err instanceof Error ? err.message : 'Failed to refund guest');
    } finally {
      setBusyGuestId(null);
    }
  };

  const handleCancelEvent = async () => {
    if (!code || cancellingEvent) return;
    const reason = cancelEventReason.trim();
    if (!reason) {
      setCancelEventError('Please give a reason — it is shown to everyone with a ticket.');
      return;
    }

    setCancellingEvent(true);
    setCancelEventError(null);
    try {
      const token = await getToken();
      const response = await fetch(`/api/rooms/${code}/cancel`, {
        method: 'POST',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({ reason }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const authMessage = signInMessageForAuthStatus(response, 'Sign in to cancel this event');
        if (authMessage) throw new Error(authMessage);
        throw new Error(data.error || 'Failed to cancel event');
      }

      setShowCancelEventModal(false);
      setCancelEventReason('');
      await fetchInvitation();
    } catch (err) {
      console.error('Error cancelling event:', err);
      setCancelEventError(err instanceof Error ? err.message : 'Failed to cancel event');
    } finally {
      setCancellingEvent(false);
    }
  };

  const handleBlockGuest = async (guest: GuestListItem) => {
    if (!code || busyGuestId) return;

    const refundText = guest.refundableAmount > 0
      ? ` This will refund ${formatMoney(guest.refundableAmount, invitation?.currency)}.`
      : '';
    if (!window.confirm(`Block ${guest.name || 'this guest'}?${refundText}`)) {
      return;
    }

    setBusyGuestId(guest.id);
    try {
      const token = await getToken();
      const response = await fetch(`/api/rooms/${code}/guests/${guest.id}/block`, {
        method: 'POST',
        headers: jsonAuthHeaders(token),
        credentials: 'same-origin',
        body: JSON.stringify({ reason: 'Blocked from the event page' }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const authMessage = signInMessageForAuthStatus(response, 'Sign in to block guests');
        if (authMessage) throw new Error(authMessage);
        throw new Error(data.error || 'Failed to block guest');
      }

      await fetchInvitation();
    } catch (err) {
      console.error('Error blocking guest:', err);
      alert(err instanceof Error ? err.message : 'Failed to block guest');
    } finally {
      setBusyGuestId(null);
    }
  };

  const handleUnblockGuest = async (guest: GuestListItem) => {
    if (!code || busyGuestId) return;

    setBusyGuestId(guest.id);
    try {
      const token = await getToken();
      const response = await fetch(`/api/rooms/${code}/guests/${guest.id}/unblock`, {
        method: 'POST',
        headers: authHeaders(token),
        credentials: 'same-origin',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const authMessage = signInMessageForAuthStatus(response, 'Sign in to unblock guests');
        if (authMessage) throw new Error(authMessage);
        throw new Error(data.error || 'Failed to unblock guest');
      }

      await fetchInvitation();
    } catch (err) {
      console.error('Error unblocking guest:', err);
      alert(err instanceof Error ? err.message : 'Failed to unblock guest');
    } finally {
      setBusyGuestId(null);
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return { date: null, time: null, relative: null };
    const date = new Date(dateString);
    const now = new Date();

    // Compare calendar days, not time difference
    const eventDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const daysDiff = Math.round((eventDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    const relative = date.getTime() < now.getTime()
      ? 'started'
      : daysDiff === 0
        ? 'today'
        : daysDiff === 1
          ? 'tomorrow'
          : daysDiff > 1
            ? `in ${daysDiff} days`
            : 'started';

    return {
      date,
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
      relative: `starts ${relative}`,
    };
  };

  const getEndDate = (start: string | null, end: string | null, durationMinutes?: number | null) => {
    if (!start) return null;
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : null;

    if (endDate && Number.isFinite(endDate.getTime()) && endDate.getTime() > startDate.getTime()) {
      return endDate;
    }

    if (durationMinutes && durationMinutes > 0) {
      return new Date(startDate.getTime() + durationMinutes * 60 * 1000);
    }

    return null;
  };

  const formatDurationLabel = (minutes: number) => {
    if (minutes < 60) return `${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
    return `${hours}h ${mins}m`;
  };

  const formatTimeRange = (start: string | null, end: string | null, durationMinutes?: number | null) => {
    if (!start) return null;
    const startDate = new Date(start);
    const startTime = startDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const endDate = getEndDate(start, end, durationMinutes);
    if (!endDate) return startTime;
    const endTime = endDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${startTime} - ${endTime}`;
  };

  const calculateDuration = (start: string | null, end: string | null, durationMinutes?: number | null) => {
    if (!start) return durationMinutes && durationMinutes > 0 ? formatDurationLabel(durationMinutes) : null;
    const endDate = getEndDate(start, end, durationMinutes);
    if (!endDate) return null;
    const diff = endDate.getTime() - new Date(start).getTime();
    const minutes = Math.round(diff / (1000 * 60));
    if (!Number.isFinite(minutes) || minutes <= 0) return null;
    return formatDurationLabel(minutes);
  };

  const getTimezoneAbbr = (tz: string | null) => {
    if (!tz) return 'Local time';
    try {
      const date = new Date();
      const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(date);
      const tzPart = parts.find(p => p.type === 'timeZoneName');
      return tzPart?.value || tz;
    } catch {
      return tz;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-line border-t-accent rounded-full animate-spin" />
      </div>
    );
  }

  const isSessionNotOpenError = error === 'This session opens five minutes before it starts.';

  if (error || !invitation) {
    return (
      <div className="min-h-screen bg-bg flex flex-col relative z-10 pt-[72px]">
        <NavBar
          variant="light"
          links={[
            { label: 'Drawing Events', href: '/events' },
            { label: 'Artists', href: '/artists' },
            { label: 'Models', href: '/models' },
          ]}
        />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <h1 className="text-2xl font-display font-medium text-text mb-2">{error || 'Invitation not found'}</h1>
            <p className="text-text-dim mb-6">
              {isSessionNotOpenError
                ? 'Return to the event page until the organizer opens the session.'
                : 'This invitation may have expired or been removed.'}
            </p>
            <button
              onClick={() => {
                if (isSessionNotOpenError) {
                  setError(null);
                  if (!invitation) {
                    setLoading(true);
                    void fetchInvitation();
                  }
                  return;
                }

                navigate('/');
              }}
              className="btn btn-accent btn-lg"
            >
              {isSessionNotOpenError ? 'Back to Event' : 'Go to Home'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (invitation.isClosed && code) {
    return <Navigate to={`/s/${code}/summary`} replace />;
  }

  const { date, time, relative } = formatDateTime(invitation.scheduledAt);
  const timeRange = formatTimeRange(invitation.scheduledAt, invitation.endsAt, invitation.duration);
  const duration = calculateDuration(invitation.scheduledAt, invitation.endsAt, invitation.duration);
  const tzAbbr = getTimezoneAbbr(invitation.timezone);
  const shareUrl = `${window.location.origin}/s/${code}`;

  const isCancelled = Boolean(invitation.cancelledAt);
  const canJoin = !isCancelled && (invitation.hasStarted || invitation.isHost || invitation.isModel);
  const hasConfirmedTicket = invitation.hasTicket && !invitation.isHost && !invitation.isModel;
  const showPendingModelInvitePanel = !invitation.isHost && invitation.isModel && invitation.modelInvite?.rsvpStatus === 'pending';
  const showRolePanel = invitation.isHost || (invitation.isModel && !showPendingModelInvitePanel);
  const showModelPaymentTerms = hasSeparateModelForPayment(invitation);
  const showPaidModelPaymentTerms = invitation.pricingType !== 'free' && showModelPaymentTerms;
  const parsedDonationAmount = parseFloat(donationAmount);
  const listedCheckoutAmountCents = invitation.pricingType === 'fixed'
    ? invitation.ticketPrice ?? 0
    : invitation.pricingType === 'donation'
      ? Number.isFinite(parsedDonationAmount)
        ? Math.max(0, Math.round(parsedDonationAmount * 100))
        : 0
      : 0;
  const platformFeeWaiverCreditCents = platformFeeInfo.platformFeeWaived && listedCheckoutAmountCents > 0
    ? Math.round(listedCheckoutAmountCents * (platformFeeInfo.feePercent / 100))
    : 0;
  const finalCheckoutAmountCents = Math.max(0, listedCheckoutAmountCents - platformFeeWaiverCreditCents);
  const pendingInvitations = invitation.guestList.filter((guest) => guest.rsvpStatus !== 'confirmed' && !guest.blockedAt);
  const hasPreparingParticipants = Boolean(preSessionStatus?.hasPreparingParticipants);
  const showPreparingScreen = Boolean(
    !invitation.hasStarted &&
    hasPreparingParticipants &&
    (waitingForSessionOpen || preSessionStatus?.shouldShowPreparation)
  );
  const showWaitingSessionScreen = Boolean(waitingForSessionOpen && !invitation.hasStarted && !showPreparingScreen);
  const acceptedModelProfiles = invitation.modelProfiles?.length
    ? invitation.modelProfiles
    : invitation.modelProfile
      ? [invitation.modelProfile]
      : [];
  const hostIsAcceptedModel = Boolean(
    invitation.hostProfile && acceptedModelProfiles.some(profile => profile.id === invitation.hostProfile?.id)
  );

  if (showPreparingScreen) {
    return (
      <>
        <PreparingSessionScreen
          eventName={invitation.name}
          names={preSessionStatus?.preparingNames ?? []}
        />
        {showRoomOpenModal && (
          <RoomOpenModal joining={joining} onEnter={handleJoin} />
        )}
      </>
    );
  }

  if (showWaitingSessionScreen) {
    return (
      <>
        <WaitingSessionScreen
          eventName={invitation.name}
          scheduledAt={invitation.scheduledAt}
          onBackToEvent={() => {
            setWaitingForSessionOpen(false);
            setPreSessionStatus(null);
          }}
        />
        {showRoomOpenModal && (
          <RoomOpenModal joining={joining} onEnter={handleJoin} />
        )}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-bg relative z-10 pt-[72px]">
      {/* Nav */}
      <NavBar
        variant="light"
        links={[
          { label: 'Drawing Events', href: '/events' },
          { label: 'Artists', href: '/artists' },
          { label: 'Models', href: '/models' },
        ]}
      />

      {/* Main Content */}
      <main className="py-14 pb-20">
        <div
          className={invitationPageContentClassName({ showPendingModelInvitePanel, showRolePanel })}
        >
          {/* Single unified sheet */}
          <div className="space-y-6">
          {refundRequestRecorded && (
            <div className="rounded-lg border border-warn/25 bg-warn/10 px-4 py-3 text-sm text-text">
              Your reservation was cancelled and a refund request was sent to the organizer, who decides whether to refund your ticket. No charge has been reversed yet.
            </div>
          )}
          {isCancelled && (
            <div className="rounded-lg border border-warn/30 bg-warn/10 px-4 py-3.5" data-testid="event-cancelled-banner">
              <div className="text-sm font-semibold text-text">This event has been cancelled</div>
              {invitation.cancellationReason && (
                <p className="text-sm text-text-dim mt-1.5">
                  Reason from the organizer: &ldquo;{invitation.cancellationReason}&rdquo;
                </p>
              )}
              <p className="text-[13px] text-text-dim mt-1.5">
                Paid tickets are refunded in full, including any fees.
              </p>
            </div>
          )}
          {showPendingModelInvitePanel && (
            <section className="rounded-[18px] border border-accent/30 bg-panel p-5 shadow-md">
              <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                <div className="max-w-none">
                  <div className="font-mono text-[11px] font-semibold uppercase tracking-wide text-accent">
                    Model invitation
                  </div>
                  <h2 className="mt-2 font-display text-[30px] font-light leading-tight text-text">
                    Can you model this session?
                  </h2>
                  <p className="mt-2 text-[14px] leading-relaxed text-text-dim">
                    Respond here so the organizer knows whether you can model. The event details are below.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:min-w-[280px]">
                  <button
                    onClick={() => handleModelRespond(true)}
                    disabled={modelResponding}
                    className="btn btn-accent justify-center"
                  >
                    Accept
                  </button>
                  <button
                    onClick={() => handleModelRespond(false)}
                    disabled={modelResponding}
                    className="btn justify-center"
                  >
                    Decline
                  </button>
                </div>
              </div>

              {showModelPaymentTerms && (invitation.modelGuarantee || invitation.modelSplitPercent || invitation.modelExpectations || invitation.revenueSplitMode === 'even') && (
                <div className="mt-5 border-t border-line-soft pt-5">
                  {invitation.modelExpectations && (
                    <p className="mb-3 text-[13px] leading-relaxed text-text-dim">
                      {invitation.modelExpectations}
                    </p>
                  )}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="rounded-lg border border-line-soft bg-panel2/60 p-3">
                      <div className="text-[11px] text-text-faint">Guarantee</div>
                      <div className="font-display text-[22px] text-ok">
                        {formatDollars((invitation.modelGuarantee ?? 0) / 100, invitation.currency)}
                      </div>
                    </div>
                    <div className="rounded-lg border border-line-soft bg-panel2/60 p-3">
                      <div className="text-[11px] text-text-faint">Split</div>
                      <div className="font-display text-[22px] text-text">
                        {invitation.revenueSplitMode === 'even' ? 'Even' : `${invitation.modelSplitPercent}%`}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowPayoutSimulator((current) => !current)}
                    className="mt-3 border-b border-text/30 pb-px text-[13px] text-text transition-colors hover:border-text"
                  >
                    {showPayoutSimulator ? 'Hide simulation' : 'Simulate'}
                  </button>
                  {showPayoutSimulator && (
                    <CompactPayoutSimulator
                      invitation={invitation}
                      role="model"
                      feePercent={platformFeeInfo.feePercent}
                      value={payoutSeatCount}
                      onChange={setPayoutSeatCount}
                    />
                  )}
                </div>
              )}
            </section>
          )}
          <article className="bg-panel border border-line rounded-[24px] shadow-xl overflow-hidden">
            {/* Hero */}
            <div className="relative aspect-[16/9] bg-panel2 overflow-hidden">
              {invitation.imageUrl ? (
                <img
                  src={invitation.imageUrl}
                  alt={invitation.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <>
                  <div className="absolute inset-0 bg-gradient-to-br from-[#f3e7d2] via-[#e7cfac] to-[#b8724a] opacity-80" />
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_40%_30%,rgba(214,153,118,0.6),transparent_60%)]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                </>
              )}

              {invitation.imageUrl && (
                <EventImageCredit
                  artistCredit={invitation.imageArtistCredit}
                  modelCredit={invitation.imageModelCredit}
                />
              )}

              {/* Invited badge */}
              {invitation.isInvited && !invitation.isBlocked && !invitation.isHost && (
                <div className="absolute top-4 left-4 z-10 inline-flex items-center gap-2 px-3 py-1.5 bg-cream/88 backdrop-blur-sm rounded-md">
                  <span className="w-[5px] h-[5px] rounded-full bg-accent" />
                  <span className="font-mono text-[10px] font-semibold uppercase text-accent">You're invited</span>
                </div>
              )}

              {/* Live badge or Countdown overlay */}
              {isCancelled ? (
                <div className="absolute top-4 right-4 z-10 inline-flex items-center gap-2 px-3 py-1.5 bg-black/70 backdrop-blur-sm rounded-md">
                  <span className="font-mono text-[10px] font-semibold uppercase text-white">Cancelled</span>
                </div>
              ) : invitation.hasStarted && !invitation.isClosed ? (
                <div className="absolute top-4 right-4 z-10 inline-flex items-center gap-2 px-3 py-1.5 bg-accent/90 backdrop-blur-sm rounded-md">
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  <span className="font-mono text-[10px] font-semibold uppercase text-white">Live Now</span>
                </div>
              ) : invitation.scheduledAt && !invitation.isClosed && date && (
                <div className="absolute bottom-4 right-4 z-10 px-4 py-2.5 bg-black/70 backdrop-blur-sm rounded-xl">
                  <Countdown targetDate={date} />
                </div>
              )}
            </div>

            {/* Body */}
            <div className="p-8 md:p-10">
              {/* Tutored tag and Host info row */}
              <div className="flex items-start justify-between gap-4 mb-5">
                {/* Host info */}
                {invitation.hostProfile?.displayName && (
                  <Link
                    to={`/u/${invitation.hostProfile.id}`}
                    className="flex items-center gap-3 text-[13.5px] text-text-dim hover:opacity-80 transition-opacity"
                  >
                    <div className="w-[32px] h-[32px] rounded-full bg-panel2 border border-line flex items-center justify-center font-display text-[14px] text-accent overflow-hidden">
                      {invitation.hostProfile.photos?.[0] ? (
                        <img
                          src={invitation.hostProfile.photos[0].url}
                          alt={invitation.hostProfile.displayName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        invitation.hostProfile.displayName.charAt(0)
                      )}
                    </div>
                    <div>
                      Hosted by{' '}
                      <strong className="text-text font-semibold">{invitation.hostProfile.displayName}</strong>
                    </div>
                  </Link>
                )}

                {/* Tutored tag */}
                {invitation.isTutored && (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-accent-soft rounded-full">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-accent">
                      <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                      <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5" />
                    </svg>
                    <span className="font-mono text-[10px] font-semibold uppercase text-accent">Tutored</span>
                  </div>
                )}
              </div>

              {/* Title */}
              <h1 className="font-display text-[clamp(32px,5vw,44px)] font-light leading-[1.08] text-accent">
                {invitation.name}
              </h1>

              {/* Description */}
              {invitation.description && (
                <p className="text-[15px] text-text-dim leading-relaxed mt-4">
                  {invitation.description}
                </p>
              )}

              {/* Date/Time and Calendar row */}
              {invitation.scheduledAt && date && (
                <div className="mt-8 flex flex-col items-start gap-4 p-4 bg-panel2/60 rounded-xl border border-line-soft sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-4">
                    {/* Calendar icon with date */}
                    <div className="w-14 h-14 flex-shrink-0 rounded-xl bg-accent-soft flex flex-col items-center justify-center">
                      <span className="font-mono text-[10px] text-accentr uppercase leading-none">
                        {date.toLocaleDateString('en-US', { month: 'short' })}
                      </span>
                      <span className="font-display text-[22px] font-normal text-accent leading-tight">
                        {date.getDate()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="font-display text-[17px] font-normal">
                        {date.toLocaleDateString('en-US', { weekday: 'long' })}
                      </div>
                      <div className="text-[14px] text-text-dim">
                        {timeRange} <span className="text-text-faint">· {tzAbbr}</span>
                        {duration && <span className="text-text-faint"> · {duration}</span>}
                      </div>
                    </div>
                  </div>
                  <CalendarButtons
                    eventName={invitation.name}
                    scheduledAt={invitation.scheduledAt}
                    endsAt={invitation.endsAt}
                    timezone={invitation.timezone}
                    roomCode={code || ''}
                  />
                </div>
              )}

              {/* Model section */}
              {acceptedModelProfiles.length > 0 && (
                <div className="mt-8">
                  <div className="font-mono text-[12px] text-text-faint uppercase font-semibold mb-4">
                    {acceptedModelProfiles.length === 1 ? 'About the model' : 'About the models'}
                  </div>
                  <div className="space-y-6">
                    {acceptedModelProfiles.map((profile, index) => (
                      <div
                        key={profile.id}
                        className={index > 0 ? 'border-t border-line-soft pt-6' : undefined}
                      >
                        <EventModelProfileCard profile={profile} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* About the organizer section - skip if host is the same as model */}
              {invitation.hostProfile?.bio && !hostIsAcceptedModel && (
                <div className="mt-8">
                  <div className="font-mono text-[12px] text-text-faint uppercase font-semibold mb-4">
                    About the organizer
                  </div>
                  <div className="flex gap-5">
                    {invitation.hostProfile.photos?.[0] && (
                      <Link
                        to={`/u/${invitation.hostProfile.id}`}
                        className="w-20 h-20 rounded-xl overflow-hidden bg-panel2 flex-shrink-0 hover:opacity-80 transition-opacity"
                      >
                        <img
                          src={invitation.hostProfile.photos[0].url}
                          alt={invitation.hostProfile.displayName}
                          className="w-full h-full object-cover"
                        />
                      </Link>
                    )}
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/u/${invitation.hostProfile.id}`}
                        className="font-display text-lg font-normal hover:text-accent transition-colors"
                      >
                        {invitation.hostProfile.displayName}
                      </Link>
                      {invitation.hostProfile.country && (
                        <div className="text-[13px] text-text-dim mt-0.5">
                          {new Intl.DisplayNames(['en'], { type: 'region' }).of(invitation.hostProfile.country)}
                        </div>
                      )}
                      <p className="text-[13px] text-text-dim mt-2 leading-relaxed">
                        {invitation.hostProfile.bio}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Attending section */}
              {invitation.attendeeCount > 0 && (
                <div className="mt-8 pt-6 border-t border-line-soft">
                  <div className="font-mono text-[12px] text-text-faint uppercase font-semibold mb-4">
                    Attending
                  </div>
                  <AttendeeGrid attendees={invitation.attendees} />
                </div>
              )}

              {/* CTA section */}
              <div className="mt-10 pt-8 border-t border-line">
                {isCancelled ? (
                  <div className="text-center py-4">
                    <p className="text-text-dim">This event has been cancelled. Reservations are no longer available.</p>
                    <Link to="/events" className="btn btn-lg mt-4">Browse other sessions</Link>
                  </div>
                ) : !invitation.isClosed ? (
                  <div className="flex flex-col gap-4">
                    <SignedIn>
                      {invitation.isHost ? (
                        <DisabledVisitorPurchaseButton
                          invitation={invitation}
                          tooltip="Button disabled, as you are the event creator"
                        />
                      ) : invitation.isModel ? (
                        <DisabledVisitorPurchaseButton invitation={invitation} />
                      ) : invitation.isBlocked ? (
                        <button
                          disabled
                          className="btn btn-lg w-full bg-panel2 text-text-dim border border-line cursor-not-allowed"
                        >
                          Reservation unavailable
                        </button>
                      ) : hasConfirmedTicket ? (
                        <>
                          <button
                            onClick={canJoin ? handleJoin : undefined}
                            disabled={joining || !canJoin}
                            className={`btn btn-lg w-full justify-center ${
                              canJoin
                                ? 'btn-accent'
                                : 'bg-panel2 text-text-dim border border-line cursor-default'
                            }`}
                          >
                            {canJoin ? (
                              <span aria-hidden="true">→</span>
                            ) : (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                            {joining ? 'Joining...' : canJoin ? 'Join session' : "You're confirmed"}
                          </button>
                          {!canJoin && (
                            <p className="text-center text-[13px] leading-relaxed text-text-dim">
                              You will be able to enter the room shortly before the start time.
                            </p>
                          )}
                        </>
                      ) : invitation.isSoldOut && invitation.userRsvpStatus !== 'confirmed' ? (
                        <button
                          disabled
                          className="btn btn-lg w-full bg-panel2 text-text-dim border border-line cursor-not-allowed"
                        >
                          Sold Out
                        </button>
                      ) : (
                        <>
                          {invitation.pricingType === 'donation' && (
                            <div className="flex items-center gap-2">
                              <span className="text-text-dim">{currencySymbol(invitation.currency)}</span>
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={donationAmount}
                                onChange={(e) => setDonationAmount(e.target.value)}
                                placeholder={invitation.suggestedMinimum ? (invitation.suggestedMinimum / 100).toString() : '0'}
                                className="input flex-1 text-lg font-semibold"
                              />
                              {invitation.suggestedMinimum && (
                                <span className="text-sm text-text-faint">
                                  suggested: ${(invitation.suggestedMinimum / 100).toFixed(0)}
                                </span>
                              )}
                            </div>
                          )}
                          {(invitation.pricingType === 'fixed' || invitation.pricingType === 'donation') && listedCheckoutAmountCents > 0 && (
                            <div className="rounded-lg border border-line bg-panel2/60 p-3 text-[13px]">
                              <div className="flex items-center justify-between gap-3 text-text-dim">
                                <span>{invitation.pricingType === 'donation' ? 'Donation' : 'Ticket'}</span>
                                <span className="tabular-nums">{formatMoney(listedCheckoutAmountCents, invitation.currency)}</span>
                              </div>
                              {platformFeeWaiverCreditCents > 0 && (
                                <div className="mt-1.5 flex items-center justify-between gap-3 text-ok">
                                  <span>{platformFeeInfo.waiverLabel}</span>
                                  <span className="tabular-nums">-{formatMoney(platformFeeWaiverCreditCents, invitation.currency)}</span>
                                </div>
                              )}
                              <div className="mt-2 flex items-center justify-between gap-3 border-t border-line-soft pt-2 text-text">
                                <span>Total</span>
                                <span className="font-medium tabular-nums">{formatMoney(finalCheckoutAmountCents, invitation.currency)}</span>
                              </div>
                            </div>
                          )}
                          <button
                            onClick={handleRsvp}
                            disabled={rsvping}
                            className="btn btn-accent btn-lg w-full"
                          >
                            {rsvping ? (
                              <>
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Processing...
                              </>
                            ) : invitation.pricingType === 'free' ? (
                              <>Reserve for Free <span aria-hidden="true">→</span></>
                            ) : invitation.pricingType === 'donation' ? (
                              <>Pay & Reserve · {formatMoney(finalCheckoutAmountCents, invitation.currency)} <span aria-hidden="true">→</span></>
                            ) : (
                              <>Buy Ticket · {formatMoney(finalCheckoutAmountCents, invitation.currency)} <span aria-hidden="true">→</span></>
                            )}
                          </button>
                        </>
                      )}
                    </SignedIn>

                    <SignedOut>
                      {showRolePanel ? (
                        <div className="text-center text-[13px] text-text-dim">
                          Invitation controls are in the panel beside the event details.
                        </div>
                      ) : invitation.isBlocked ? (
                        <button
                          disabled
                          className="btn btn-lg w-full bg-panel2 text-text-dim border border-line cursor-not-allowed"
                        >
                          Reservation unavailable
                        </button>
                      ) : invitation.isSoldOut ? (
                        <button
                          disabled
                          className="btn btn-lg w-full bg-panel2 text-text-dim border border-line cursor-not-allowed"
                        >
                          Sold Out
                        </button>
                      ) : (
                        <button
                          onClick={() => navigate(`/sign-in?redirect_url=/s/${code}`)}
                          className="btn btn-accent btn-lg w-full"
                        >
                          Sign in to reserve <span aria-hidden="true">→</span>
                        </button>
                      )}
                    </SignedOut>

                    {!hasConfirmedTicket && (
                      <div className="text-center text-[13px] text-text-faint">
                        Can't make it? <Link to="/" className="text-text-dim underline underline-offset-2 decoration-text-dim/30 hover:text-text">Browse other sessions</Link>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-text-dim">This session has ended.</p>
                    <Link to="/" className="btn btn-lg mt-4">Browse sessions</Link>
                  </div>
                )}
              </div>
            </div>
          </article>

          {invitation.isHost && invitation.modelOption === 'ad' && (
            <section id="applications">
              <ApplicationsSection
                roomCode={invitation.code}
                onModelSelected={fetchInvitation}
              />
            </section>
          )}
          </div>

          {showRolePanel && (
            <aside className={invitationPageRolePanelClassName()}>
              {invitation.isHost ? (
                <>
                  <SidePanelSection title="You're organizing this event" titleVariant="medium">
                    <div className="space-y-2.5">
                      {isCancelled ? (
                        <div className="rounded-lg bg-warn/10 border border-warn/25 p-3 text-[13px] text-text-dim">
                          You cancelled this event. Ticket holders and models have been notified and paid tickets are refunded.
                        </div>
                      ) : !invitation.isClosed ? (
                        <button
                          onClick={handleJoin}
                          disabled={joining}
                          className="btn btn-accent w-full justify-center"
                        >
                          {joining ? 'Entering...' : 'Enter Room'}
                        </button>
                      ) : (
                        <div className="rounded-lg bg-panel2/60 border border-line-soft p-3 text-[13px] text-text-dim">
                          This session has ended.
                        </div>
                      )}
                      {!isCancelled && (
                        <button
                          type="button"
                          onClick={() => navigate('/create', { state: { roomCode: code, section: 'details' } })}
                          className="btn w-full justify-center"
                        >
                          Edit Event Details
                        </button>
                      )}
                      <ShareLinkField url={shareUrl} copied={copied} onCopy={handleCopyLink} />
                      {!isCancelled && !invitation.isClosed && !invitation.hasStarted && (
                        <button
                          type="button"
                          onClick={() => {
                            setCancelEventError(null);
                            setShowCancelEventModal(true);
                          }}
                          className="btn w-full justify-center text-warn border-warn/40 hover:border-warn"
                        >
                          Cancel event
                        </button>
                      )}
                    </div>
                  </SidePanelSection>

                  <SidePanelSection title="Stats">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-lg bg-panel2/60 border border-line-soft p-3">
                        <div className="text-[11px] text-text-faint">Tickets sold</div>
                        <div className="font-display text-[24px] text-text">{invitation.ticketCount}</div>
                      </div>
                      <div className="rounded-lg bg-panel2/60 border border-line-soft p-3">
                        <div className="text-[11px] text-text-faint">Capacity</div>
                        <div className="font-display text-[24px] text-text">
                          {invitation.maxAttendees ?? 'Open'}
                        </div>
                      </div>
                    </div>
                    <p className="mt-3 text-[13px] text-text-dim">
                      Model invitation:{' '}
                      <span className="capitalize text-text">{invitation.modelInvite?.rsvpStatus ?? 'none'}</span>
                    </p>
                  </SidePanelSection>

                  {showPaidModelPaymentTerms && (invitation.modelGuarantee || invitation.modelSplitPercent || invitation.revenueSplitMode === 'even') && (
                    <SidePanelSection title="Payment">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-lg bg-panel2/60 border border-line-soft p-3">
                          <div className="text-[11px] text-text-faint">Guarantee</div>
                          <div className="font-display text-[22px] text-ok">
                            {formatDollars((invitation.modelGuarantee ?? 0) / 100, invitation.currency)}
                          </div>
                        </div>
                        <div className="rounded-lg bg-panel2/60 border border-line-soft p-3">
                          <div className="text-[11px] text-text-faint">Split</div>
                          <div className="font-display text-[22px] text-text">
                            {invitation.revenueSplitMode === 'even'
                              ? 'Even'
                              : `${100 - invitation.modelSplitPercent}% / ${invitation.modelSplitPercent}%`}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowPayoutSimulator((current) => !current)}
                        className="mt-3 text-[13px] text-text border-b border-text/30 pb-px hover:border-text transition-colors"
                      >
                        {showPayoutSimulator ? 'Hide simulation' : 'Simulate'}
                      </button>
                      {showPayoutSimulator && (
                        <CompactPayoutSimulator
                          invitation={invitation}
                          role="host"
                          feePercent={platformFeeInfo.feePercent}
                          value={payoutSeatCount}
                          onChange={setPayoutSeatCount}
                        />
                      )}
                    </SidePanelSection>
                  )}

                  <RefundRequestsPanel
                    roomId={invitation.id}
                    getToken={getToken}
                    onResolved={fetchInvitation}
                  />

                  <SidePanelSection title="Guest list">
                    <GuestList
                      guests={invitation.guestList}
                      currency={invitation.currency}
                      canManage
                      onRefund={openRefundModal}
                      onBlock={handleBlockGuest}
                      onUnblock={handleUnblockGuest}
                      busyGuestId={busyGuestId}
                    />
                  </SidePanelSection>

                  <SidePanelSection title="Invitations">
                    <InvitationsPanel
                      invitations={pendingInvitations}
                      roomCode={code || invitation.code}
                      getToken={getToken}
                      onRefresh={fetchInvitation}
                    />
                  </SidePanelSection>
                </>
              ) : (
                <>
                  <SidePanelSection
                    title={
                      invitation.modelInvite?.rsvpStatus === 'pending'
                        ? 'Model invitation'
                        : "You're modeling at this event"
                    }
                    titleVariant={invitation.modelInvite?.rsvpStatus === 'pending' ? 'label' : 'medium'}
                  >
                    {invitation.modelInvite?.rsvpStatus === 'pending' ? (
                      <div className="space-y-2.5">
                        <p className="text-[13px] text-text-dim">
                          Respond to this invitation so the organizer knows whether you can model.
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={() => handleModelRespond(true)}
                            disabled={modelResponding}
                            className="btn btn-accent justify-center"
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => handleModelRespond(false)}
                            disabled={modelResponding}
                            className="btn justify-center"
                          >
                            Decline
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2.5">
                        {isCancelled ? (
                          <div className="rounded-lg bg-warn/10 border border-warn/25 p-3 text-[13px] text-text-dim">
                            This event has been cancelled by the organizer.
                          </div>
                        ) : !invitation.isClosed ? (
                          <button
                            onClick={handleJoin}
                            disabled={joining}
                            className="btn btn-accent w-full justify-center"
                          >
                            {joining ? 'Entering...' : 'Enter Room'}
                          </button>
                        ) : (
                          <div className="rounded-lg bg-panel2/60 border border-line-soft p-3 text-[13px] text-text-dim">
                            This session has ended.
                          </div>
                        )}
                      </div>
                    )}
                    <div className="mt-2.5">
                      <ShareLinkField url={shareUrl} copied={copied} onCopy={handleCopyLink} />
                    </div>
                  </SidePanelSection>

                  {showModelPaymentTerms && (invitation.modelGuarantee || invitation.modelSplitPercent || invitation.modelExpectations || invitation.revenueSplitMode === 'even') && (
                    <SidePanelSection title="Payment">
                      {invitation.modelExpectations && (
                        <p className="text-[13px] text-text-dim leading-relaxed mb-3">
                          {invitation.modelExpectations}
                        </p>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-lg bg-panel2/60 border border-line-soft p-3">
                          <div className="text-[11px] text-text-faint">Guarantee</div>
                          <div className="font-display text-[22px] text-ok">
                            {formatDollars((invitation.modelGuarantee ?? 0) / 100, invitation.currency)}
                          </div>
                        </div>
                        <div className="rounded-lg bg-panel2/60 border border-line-soft p-3">
                          <div className="text-[11px] text-text-faint">Split</div>
                          <div className="font-display text-[22px] text-text">
                            {invitation.revenueSplitMode === 'even' ? 'Even' : `${invitation.modelSplitPercent}%`}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowPayoutSimulator((current) => !current)}
                        className="mt-3 text-[13px] text-text border-b border-text/30 pb-px hover:border-text transition-colors"
                      >
                        {showPayoutSimulator ? 'Hide simulation' : 'Simulate'}
                      </button>
                      {showPayoutSimulator && (
                        <CompactPayoutSimulator
                          invitation={invitation}
                          role="model"
                          feePercent={platformFeeInfo.feePercent}
                          value={payoutSeatCount}
                          onChange={setPayoutSeatCount}
                        />
                      )}
                    </SidePanelSection>
                  )}

                  <SidePanelSection title="Guest list">
                    <GuestList
                      guests={invitation.guestList}
                      canManage
                      onRefund={openRefundModal}
                      onBlock={handleBlockGuest}
                      onUnblock={handleUnblockGuest}
                      busyGuestId={busyGuestId}
                    />
                  </SidePanelSection>
                </>
              )}
            </aside>
          )}
        </div>
      </main>

      {refundGuest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4">
          <div className="w-full max-w-md rounded-xl border border-line bg-panel p-5 shadow-xl">
            <h2 className="font-display text-[28px] leading-tight text-text">Refund guest</h2>
            <p className="mt-2 text-[13px] text-text-dim">
              {refundGuest.name || refundGuest.email || 'Guest'} has {formatMoney(refundGuest.refundableAmount, invitation?.currency)} available to refund.
            </p>

            <div className="mt-4 space-y-2">
              {[
                { value: 'full', label: `Full refund (${formatMoney(refundGuest.refundableAmount, invitation?.currency)})` },
                { value: 'half', label: `Half refund (${formatMoney(Math.max(1, Math.round(refundGuest.refundableAmount / 2)), invitation?.currency)})` },
                { value: 'custom', label: 'Custom amount' },
              ].map((option) => (
                <label key={option.value} className="flex items-center gap-2 rounded-lg border border-line-soft bg-panel2/40 px-3 py-2 text-[13px] text-text">
                  <input
                    type="radio"
                    name="refund-mode"
                    value={option.value}
                    checked={refundMode === option.value}
                    onChange={() => setRefundMode(option.value as 'full' | 'half' | 'custom')}
                  />
                  {option.label}
                </label>
              ))}
            </div>

            {refundMode === 'custom' && (
              <label className="mt-3 block">
                <span className="text-[13px] font-medium text-text">Amount</span>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-text-dim">{currencySymbol(invitation.currency)}</span>
                  <input
                    type="number"
                    min="0.01"
                    max={(refundGuest.refundableAmount / 100).toFixed(2)}
                    step="0.01"
                    value={customRefundAmount}
                    onChange={(event) => setCustomRefundAmount(event.target.value)}
                    className="input flex-1"
                  />
                </div>
              </label>
            )}

            <label className="mt-3 block">
              <span className="text-[13px] font-medium text-text">Note</span>
              <textarea
                value={refundReason}
                onChange={(event) => setRefundReason(event.target.value)}
                rows={3}
                placeholder="Optional internal note"
                className="mt-1 w-full resize-none rounded-lg border border-line bg-panel px-3 py-2 text-[13px] text-text outline-none placeholder:text-text-faint focus:border-text/40"
              />
            </label>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setRefundGuest(null)}
                disabled={busyGuestId === refundGuest.id}
                className="btn disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitRefund}
                disabled={busyGuestId === refundGuest.id}
                className="btn btn-accent disabled:opacity-50"
              >
                {busyGuestId === refundGuest.id ? 'Refunding...' : `Refund ${formatMoney(getRefundAmountCents(), invitation?.currency)}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelEventModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4">
          <div className="w-full max-w-md rounded-xl border border-line bg-panel p-5 shadow-xl">
            <h2 className="font-display text-[28px] leading-tight text-text">Cancel this event?</h2>
            <p className="mt-2 text-[13px] text-text-dim">
              This cannot be undone. Everyone with a ticket is notified and paid tickets are refunded in full,
              including any fees. Invited models are notified that the event is off.
            </p>

            <label className="mt-4 block">
              <span className="text-[13px] font-medium text-text">Reason for cancelling</span>
              <textarea
                value={cancelEventReason}
                onChange={(event) => setCancelEventReason(event.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="e.g. Our model is unwell and we could not find a replacement."
                className="mt-1 w-full resize-none rounded-lg border border-line bg-panel px-3 py-2 text-[13px] text-text outline-none placeholder:text-text-faint focus:border-text/40"
              />
            </label>
            <p className="mt-2 text-[12.5px] text-warn">
              This reason is public: it appears on the event page and in the cancellation emails.
            </p>

            {cancelEventError && (
              <p className="mt-2 text-[13px] text-warn">{cancelEventError}</p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowCancelEventModal(false)}
                disabled={cancellingEvent}
                className="btn disabled:opacity-50"
              >
                Keep event
              </button>
              <button
                type="button"
                onClick={handleCancelEvent}
                disabled={cancellingEvent || !cancelEventReason.trim()}
                className="btn btn-accent disabled:opacity-50"
              >
                {cancellingEvent ? 'Cancelling...' : 'Confirm cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRoomOpenModal && (
        <RoomOpenModal joining={joining} onEnter={handleJoin} />
      )}

      {showModelPrepModal && (
        <ModelPrepModal
          joining={joining}
          onClose={() => setShowModelPrepModal(false)}
          onEnter={handleJoin}
        />
      )}

      <SharedFooter />
    </div>
  );
}
